/**
 * Core: Canvas imports
 * -----------------------------------------------------------------------------
 * Import async components, global components, and Vuex stores for use in
 * CanvasApp Vue instance.
 *
 */
/* eslint-disable no-inline-comments, quote-props */
import { defineAsyncComponent } from 'vue'

/**
 * Get async components and imports.
 * - Must be manually updated with new async components (components/async/).
 * - Previously used a dynamic import statement which caused infinite build
 *   issue and generated unused files.
 * - Only parent components should be imported.
 * - Do not import components that are only ever used as a child component.
 * - Do not import async sub-components.
 * @returns {Object}
 */
export function getAsyncComponents() {
  return {
    // canvas-async-object-start
    'accordion-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.accordion-section' */'~async/accordion-section/accordion-section') }),
    'article-card': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.article-card' */'~async/article-card/article-card') }),
    'asymmetric-image': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.asymmetric-image' */'~async/asymmetric-image/asymmetric-image') }),
    'banner-hero': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.banner-hero' */'~async/banner-hero/banner-hero') }),
    'cart-drawer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.cart-drawer' */'~async/cart-drawer/cart-drawer') }),
    'collection-hero': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.collection-hero' */'~async/collection-hero/collection-hero') }),
    'contact-us': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.contact-us' */'~async/contact-us/contact-us') }),
    'country-selector': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.country-selector' */'~async/country-selector/country-selector') }),
    'customers-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-account' */'~async/customers-account/customers-account') }),
    'customers-activate-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-activate-account' */'~async/customers-activate-account/customers-activate-account') }),
    'customers-addresses': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-addresses' */'~async/customers-addresses/customers-addresses') }),
    'customers-login': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-login' */'~async/customers-login/customers-login') }),
    'customers-order': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-order' */'~async/customers-order/customers-order') }),
    'customers-orders': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-orders' */'~async/customers-orders/customers-orders') }),
    'customers-register': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-register' */'~async/customers-register/customers-register') }),
    'customers-reset-password': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-reset-password' */'~async/customers-reset-password/customers-reset-password') }),
    'featured-content-new': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.featured-content-new' */'~async/featured-content-new/featured-content-new.vue') }),
    'featured-content': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.featured-content' */'~async/featured-content/featured-content') }),
    'featured-details': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.featured-details' */'~async/featured-details/featured-details') }),
    'featured-details-new': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.featured-details-new' */'~async/featured-details-new/featured-details-new') }),
    'image-grid': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.image-grid' */'~async/image-grid/image-grid') }),
    'main-404': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-404' */'~async/main-404/main-404') }),
    'main-article': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-article' */'~async/main-article/main-article') }),
    'main-blog': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-blog' */'~async/main-blog/main-blog') }),
    'main-cart': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-cart' */'~async/main-cart/main-cart') }),
    'main-collection': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-collection' */'~async/main-collection/main-collection') }),
    'main-list-collections': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-list-collections' */'~async/main-list-collections/main-list-collections') }),
    'main-product': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-product' */'~async/main-product/main-product') }),
    'main-search': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-search' */'~async/main-search/main-search') }),
    'media-blocks': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.media-blocks' */'~async/media-blocks/media-blocks') }),
    'media-blocks-new': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.media-blocks-new' */'~async/media-blocks-new/media-blocks-new') }),
    'menu-block': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.menu-block' */'~async/menu-block/menu-block') }),
    'product-carousel': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-carousel' */'~async/product-carousel/product-carousel') }),
    'product-filters': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-filters' */'~async/product-filters/product-filters') }),
    'product-gallery': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-gallery' */'~async/product-gallery/product-gallery') }),
    'simple-links': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.simple-links' */'~async/simple-links/simple-links') }),
    'site-footer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.site-footer' */'~async/site-footer/site-footer') }),
    'size-guide': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.size-guide' */'~async/size-guide/size-guide') }),
    'spacer-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.spacer-section' */'~async/spacer-section/spacer-section') }),
    'splash-snippet': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.splash-snippet' */'~async/splash-snippet/splash-snippet') }),
    'stats-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.stats-section' */'~async/stats-section/stats-section') }),
    'technical-details': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.technical-details' */'~async/technical-details/technical-details') }),
    'testimonial-carousel': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.testimonial-carousel' */'~async/testimonial-carousel/testimonial-carousel') }),
    'text-accordion': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.text-accordion' */'~async/text-accordion/text-accordion') }),
    'text-block': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.text-block' */'~async/text-block/text-block') }),
    // canvas-async-object-end
  }
}

/**
 * Import global components here.
 */
// canvas-global-import-start
import Accessibility from '~global/core/accessibility'
import ArtDirection from '~global/images/art-direction'
import Btn from '~global/btn/btn'
import Index from '~global/core/index'
import KlaviyoFooter from '~global/klaviyo-footer/klaviyo-footer'
import MegaNav from '~global/mega-nav/mega-nav'
import Overlay from '~global/overlay/overlay'
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'
import SiteHeader from '~global/site-header/site-header'
import marketSwitcher from '~global/market-switcher/market-switcher'
// canvas-global-import-end

/**
 * Get global components and imports.
 * - Must be manually updated with new global components (components/global).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 * @returns {Object}
 */
export function getGlobalComponents() {
  return {
    // canvas-global-object-start
    'accessibility': Accessibility,
    'art-direction': ArtDirection,
    'btn': Btn,
    'index': Index,
    'klaviyo-footer': KlaviyoFooter,
    'market-switcher': marketSwitcher,
    'mega-nav': MegaNav,
    'overlay': Overlay,
    'product-prices': ProductPrices,
    'responsive-image': ResponsiveImage,
    'site-header': SiteHeader,
    // canvas-global-object-end
  }
}

/**
 * Import Vuex stores here.
 * - Import with Store suffix to avoid conflicts with global component imports.
 */
// canvas-stores-import-start
import AccessibilityStore from '~stores/accessibility'
import CartStore from '~stores/cart'
import ComponentsStore from '~stores/components'
import IndexStore from '~stores/index'
import OverlaysStore from '~stores/overlays'
import SearchStore from '~stores/search'
import MarketSwitcher from '~stores/market-switcher'
// canvas-stores-import-end

/**
 * Get Vuex stores and imports.
 * - Must be manually updated with new Vuex stores (stores/).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 */
export function getVuexStores() {
  return {
    // canvas-stores-object-start
    'accessibility': AccessibilityStore,
    'cart': CartStore,
    'components': ComponentsStore,
    'index': IndexStore,
    'overlays': OverlaysStore,
    'search': SearchStore,
    'market-switcher': MarketSwitcher,
    // canvas-stores-object-end
  }
}
